import React, {createContext, PropsWithChildren, useContext} from 'react'
import merge from 'lodash/merge'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import {fields as styleFields, UnnormalizedProps as UnnormalizedStyleProps} from '@yosmanyga/core-style'
import type {ThemeVariablesType} from "./variables";
import type {ThemeBuildType} from './ThemeType'
import {compile} from './compile'

const ThemeContext = createContext<ThemeContextProps>({
    variables: undefined,
    build: () => {
        return {}
    },
})

type ThemeContextProps = {
    variables: ThemeVariablesType | undefined,
    build: ThemeBuildType,
}

const ThemeProvider = ({
    variables,
    build,
    children
}: ThemeProviderProps) => {
    const parent = useContext(ThemeContext)

    if (
        variables
        && typeof variables !== 'object'
    ) {
        throw new Error(`Invalid theme variables of type ${typeof variables}`)
    }

    variables = typeof variables !== 'undefined'
        // Merge if there is a parent
        ? {
            ...(parent.variables ? parent.variables : {}),
            ...variables
        }
        // Just parent variables if variables is undefined
        : parent.variables

    return <ThemeContext.Provider value={{
        variables: variables,
        build: () => {
            return build(variables, parent.build)
        },
    }}>
        {children}
    </ThemeContext.Provider>
}

type ThemeProviderProps = PropsWithChildren<ThemeContextProps>

/**
 *
 * @param identifier Component identifier in theme
 * @param props      Component props
 */
const useTheme = (
    identifier: string,
    props?: { [key: string]: unknown }
): [UnnormalizedStyleProps & {theme: {variables: ThemeVariablesType}}, object] => {
    const context = useContext(ThemeContext)

    if (!context.variables) {
        throw new Error('No theme variables set')
    }

    if (!context.build) {
        throw new Error('No theme build set')
    }

    const customProps = omit(props, styleFields)
    const styleProps = pick(props, styleFields)

    const variables = context.variables
    const build = context.build

    const built: { [key: string]: any } = build(variables)

    const themeProps = identifier in built ? built[identifier] : {}

    const compiledThemeProps = compile(
        themeProps,
        customProps,
        styleFields
    )

    const mergedProps = merge(
        {},
        compiledThemeProps,
        styleProps,

        // Used on styling
        {
            theme: {
                variables: variables,
            }
        }
    )

    return [
        mergedProps,
        customProps
    ]
}

const useVariables = (): ThemeVariablesType => {
    const context = useContext(ThemeContext)

    if (!context.variables) {
        throw new Error('No theme variables set')
    }

    return context.variables
}

export {
    ThemeProvider,
    useTheme,
    useVariables,
}